import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import bcrypt from "bcryptjs";
import Login_bg from "../../assets/images/truck.png";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Button from "../../components/Button";
import { successMessage, errorMessage } from "../../components/Toaster";
import { getErrorMessage } from "../../utils/error";
import DialogModal from "../../components/Dialog";
import logo from "../../assets/images/logo.png";
import {
  MANAGER,
  ADMIN,
  APPUSER,
  SUPER_ADMIN,
  VISO_SUPER_ADMIN,
  DASHBOARD_EDITOR,
} from "../../constants";
import { login, changePassword } from "./api";
import { useDispatch } from "react-redux";
import { initModuleFromLogin } from "../../store/slices/moduleSlice";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isPasswordSet, setIsPasswordSet] = useState(true);
  const [openModal, setOpenModal] = useState(false);

  // NEW — independent show/hide state for each password field
  const [showPassword, setShowPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validationSchema = Yup.object({
    userName: Yup.string().required("User name is required"),
    password: Yup.string().required("Password is required"),
    oldPassword: Yup.string().when("isPasswordSet", {
      is: false,
      then: (schema) => schema.required("Old password is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    newPassword: Yup.string().when("isPasswordSet", {
      is: false,
      then: (schema) =>
        schema
          .required("New password is required")
          .min(8, "Password must be at least 8 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    confirmPassword: Yup.string().when("isPasswordSet", {
      is: false,
      then: (schema) =>
        schema
          .oneOf([Yup.ref("newPassword")], "Passwords must match")
          .required("Confirm password is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleLogin = async (values) => {
    try {
      setLoading(true);
      const response = await login({
        user_name: values.userName,
        password: values.password,
      });
      const {
        is_password_set,
        user_id,
        user_name,
        first_name,
        last_name,
        email,
        phone_number,
        company_id,
        company_name,
        yard_id,
        yard_name,
        role_name,
        jwt_token,
        asset_types,
        company_logo,
        company_logo_base64,
        site_type,
        available_modules,
      } = response.data;

      setLoading(false);

      if (
        role_name !== MANAGER &&
        role_name !== ADMIN &&
        role_name !== APPUSER &&
        role_name !== SUPER_ADMIN &&
        role_name !== VISO_SUPER_ADMIN &&
        role_name !== DASHBOARD_EDITOR
      ) {
        errorMessage("Access denied: You do not have the required role.");
        return;
      }

      localStorage.setItem("user_id", user_id);
      localStorage.setItem("user_name", user_name);
      localStorage.setItem("company_id", company_id);
      localStorage.setItem("company_name", company_name);
      localStorage.setItem("auth_token", jwt_token);
      localStorage.setItem("first_name", first_name);
      localStorage.setItem("last_name", last_name);
      localStorage.setItem("email", email || "");
      localStorage.setItem("phone_number", phone_number || "");
      localStorage.setItem("role_name", role_name);
      localStorage.setItem("yard_name", yard_name);
      localStorage.setItem("yard_id", yard_id);
      localStorage.setItem("asset_type", asset_types[0]);
      localStorage.setItem("company_logo", company_logo);
      localStorage.setItem("company_logo_base64", company_logo_base64 || "");
      localStorage.setItem(
        "available_modules",
        JSON.stringify(available_modules || [site_type || "YARD"]),
      );
      localStorage.setItem("active_module", site_type || "YARD");

      dispatch(
        initModuleFromLogin({
          roleName: role_name,
          siteType: site_type,
          availableModules: available_modules,
        }),
      );
      if (is_password_set) {
        successMessage(response.message);
        navigate("/home");
      } else {
        setIsPasswordSet(false);
      }
    } catch (error) {
      setLoading(false);
      const message = getErrorMessage(error);
      errorMessage(message || "Login failed.");
    }
  };

  const handleChangePassword = async (values) => {
    if (values.oldPassword === values.newPassword) {
      errorMessage("New password cannot be the same as the old password.");
      return;
    }

    if (values.newPassword !== values.confirmPassword) {
      errorMessage("New password and confirm password must be the same.");
      return;
    }

    try {
      setLoading(true);
      const hashedPassword = await bcrypt.hash(values.newPassword, 10);
      const response = await changePassword({
        user_name: values.userName,
        old_password: values.oldPassword,
        new_password: hashedPassword,
      });
      setLoading(false);
      successMessage(response.message);
      setIsPasswordSet(true);
    } catch (error) {
      setLoading(false);
      const message = getErrorMessage(error);
      errorMessage(message || "Password change failed.");
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  // NEW — reusable eye-icon adornment for a given visibility state/toggle
  const passwordAdornment = (visible, toggle) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={toggle}
          edge="end"
          size="small"
          tabIndex={-1}
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </InputAdornment>
    ),
  });

  return (
    <div className="APP-background">
      <div
        className="d-flex justify-start flex-direction-row"
        style={{ height: "100vh" }}
      >
        <div className="flex-grow-1 bg-blue h-100vh d-flex justify-between flex-direction-column">
          <div className="d-flex justify-center">
            <img
              src={Login_bg}
              alt="logo"
              className="w-100 h-100vh object-cover"
            />
          </div>
        </div>
        <Formik
          initialValues={{
            userName: "",
            password: "",
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            if (isPasswordSet) {
              handleLogin(values);
            } else {
              handleChangePassword(values);
            }
          }}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            handleSubmit,
            resetForm,
          }) => (
            <Form
              onSubmit={handleSubmit}
              style={{
                width: "100%",
                maxWidth: "500px",
                minWidth: "350px",
                height: "100vh",
                overflowY: "auto",
                padding: "20px",
                boxSizing: "border-box",
              }}
            >
              <div
                className="d-flex justify-center align-center flex-direction-column"
                style={{
                  minHeight: "100%",
                  padding: "20px 0",
                  boxSizing: "border-box",
                }}
              >
                <div className="d-flex flex-direction-column justify-center align-center">
                  {/* <img
                    src={logo}
                    alt="App Logo"
                    style={{
                      width: "200px",
                      maxWidth: "80%",
                      marginBottom:
                        window.innerHeight < 800
                          ? "30px"
                          : window.innerHeight < 900
                            ? "50px"
                            : "80px",
                      height: "auto",
                    }}
                  /> */}
                  <h3
                    className="APP-color"
                    style={{ marginBottom: "8px", textAlign: "center" }}
                  >
                    {isPasswordSet ? "Login" : "Change Password"}
                  </h3>
                  <p
                    style={{
                      marginBottom: "20px",
                      textAlign: "center",
                      fontSize: "14px",
                    }}
                  >
                    {isPasswordSet
                      ? "Enter your credentials to log in"
                      : "Enter details to change your password"}
                  </p>
                </div>
                <TextField
                  className="w-300px m-t-20"
                  label="User Name"
                  onChange={(e) => setFieldValue("userName", e.target.value)}
                  variant="standard"
                  value={values.userName}
                  error={touched.userName && Boolean(errors.userName)}
                  helperText={touched.userName && errors.userName}
                  style={{ width: "100%", maxWidth: "300px" }}
                />
                {isPasswordSet ? (
                  <TextField
                    className="w-300px m-t-10"
                    label="Password"
                    onChange={(e) => setFieldValue("password", e.target.value)}
                    variant="standard"
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    slotProps={{
                      input: passwordAdornment(showPassword, () =>
                        setShowPassword((prev) => !prev),
                      ),
                    }}
                    style={{
                      width: "100%",
                      maxWidth: "300px",
                      marginTop: "10px",
                    }}
                  />
                ) : (
                  <>
                    <TextField
                      required
                      className="w-300px m-t-10"
                      label="Old Password"
                      onChange={(e) =>
                        setFieldValue("oldPassword", e.target.value)
                      }
                      variant="standard"
                      type={showOldPassword ? "text" : "password"}
                      value={values.oldPassword}
                      error={touched.oldPassword && Boolean(errors.oldPassword)}
                      helperText={touched.oldPassword && errors.oldPassword}
                      slotProps={{
                        input: passwordAdornment(showOldPassword, () =>
                          setShowOldPassword((prev) => !prev),
                        ),
                      }}
                      style={{
                        width: "100%",
                        maxWidth: "300px",
                        marginTop: "10px",
                      }}
                    />
                    <TextField
                      required
                      className="w-300px m-t-10"
                      label="New Password"
                      onChange={(e) =>
                        setFieldValue("newPassword", e.target.value)
                      }
                      variant="standard"
                      type={showNewPassword ? "text" : "password"}
                      value={values.newPassword}
                      error={touched.newPassword && Boolean(errors.newPassword)}
                      helperText={touched.newPassword && errors.newPassword}
                      slotProps={{
                        input: passwordAdornment(showNewPassword, () =>
                          setShowNewPassword((prev) => !prev),
                        ),
                      }}
                      style={{
                        width: "100%",
                        maxWidth: "300px",
                        marginTop: "10px",
                      }}
                    />
                    <TextField
                      required
                      className="w-300px m-t-10"
                      label="Confirm Password"
                      onChange={(e) =>
                        setFieldValue("confirmPassword", e.target.value)
                      }
                      variant="standard"
                      type={showConfirmPassword ? "text" : "password"}
                      value={values.confirmPassword}
                      error={
                        touched.confirmPassword &&
                        Boolean(errors.confirmPassword)
                      }
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      slotProps={{
                        input: passwordAdornment(showConfirmPassword, () =>
                          setShowConfirmPassword((prev) => !prev),
                        ),
                      }}
                      style={{
                        width: "100%",
                        maxWidth: "300px",
                        marginTop: "10px",
                      }}
                    />
                  </>
                )}
                <Button
                  className="white w-300px m-t-10"
                  color="primary"
                  variant="contained"
                  type="submit"
                  loading={loading}
                  disabled={loading}
                  label={
                    loading
                      ? "Processing..."
                      : isPasswordSet
                        ? "Login"
                        : "Change Password"
                  }
                  style={{
                    width: "100%",
                    maxWidth: "300px",
                    marginTop: "20px",
                  }}
                />
                <p
                  className="text-center w-100"
                  style={{ marginTop: "15px", fontSize: "14px" }}
                >
                  {isPasswordSet ? "Forgot your password?" : "Back to login?"}{" "}
                  <Button
                    onClick={() => {
                      if (isPasswordSet) {
                        handleOpenModal();
                      } else {
                        setIsPasswordSet(true);
                        resetForm();
                      }
                    }}
                    color="primary"
                    label={isPasswordSet ? "Click Here" : "Login"}
                  />
                </p>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <DialogModal
        open={openModal}
        onClose={handleCloseModal}
        onConfirm={handleCloseModal}
        title="Contact your Administrator"
        message="Please reach out to your administrator for assistance."
        confirmButtonText="OK"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export default Login;